import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Helper function for currency formatting
const formatCurrency = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
};

// Table component used by BalanceSheetTab
const Table = ({ headers, keys, data, formatters, boldRows = [] }) => (<div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"><thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{headers.map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{h}</th>)}</tr></thead><tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">{data.map((row, rowIndex) => (<tr key={rowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${boldRows.includes(rowIndex) ? 'font-bold' : ''}`}>{keys.map(key => (<td key={key} className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatters[key] ? formatters[key](row[key]) : row[key]}</td>))}</tr>))}</tbody></table></div>);

// Explanation component used by BalanceSheetTab
const Explanation = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
        <div className="mt-8 border-t-2 border-gray-200 dark:border-gray-700 explanation-section">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
                <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h4>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-4 animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

// Main BalanceSheetTab component
const BalanceSheetTab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Balance Sheet</h2><Table headers={['Year', 'Total Assets', 'Total Liabilities', 'Total Equity', 'Total Liab. & Equity', 'Check']} keys={['year', 'totalAssets', 'totalLiabilities', 'totalEquity', 'totalLiabilitiesAndEquity', 'check']} data={outputs.balanceSheet || []} formatters={{'totalAssets': formatCurrency, 'totalLiabilities': formatCurrency, 'totalEquity': formatCurrency, 'totalLiabilitiesAndEquity': formatCurrency, 'check': (v) => formatCurrency(v, 0)}} />
    <Explanation title="How This Works">
        <p>The Balance Sheet provides a snapshot of the project's financial position at a single point in time (the end of each year).</p>
        <p><strong>Principle:</strong> It is built on the fundamental accounting equation: <strong>Assets = Liabilities + Equity</strong>. Assets are what the company owns (the solar plant, cash). Liabilities are what it owes (loans). Equity is the value belonging to the owners. The "Check" column should always be zero, ensuring the model is balanced.</p>
        <p><strong>Goal:</strong> To show the company's net worth and how its assets are financed. It provides a clear picture of the project's financial health and structure year over year.</p>
    </Explanation>
</div>);

export default BalanceSheetTab; 